import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Wand20Regular, Copy20Regular, SquareAdd20Regular, TextAlignLeft20Regular, TextBulletList20Regular, TableSimple20Regular, Add20Regular } from '@fluentui/react-icons';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { RewriteNote, clearRewrittenNote, PatchNote, PostNote } from '../../redux/dashboardSlice';
import styles from './index.module.css';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import SafeHTML from '../SafeHTML';

const RewriteAIModal = ({ isOpen, onOpenChange, note, notepadId, onReplace, aiMode = 'rewrite', setShowObsSidebar, setShowTaskSidebar, setShowIRLSidebar }) => {
    const dispatch = useDispatch();
    const { rewriteNoteLoading, rewrittenNote } = useSelector(state => state.dashboard);
    const [originalText, setOriginalText] = React.useState('');
    const [hasError, setHasError] = React.useState(false);

    // Extract individual tasks from the AI HTML output when in "tasks" mode
    const extractedTasks = React.useMemo(() => {
        if (aiMode !== 'tasks' || !rewrittenNote) return [];

        // Handle explicit "no tasks" response from the backend prompt
        if (rewrittenNote.trim() === 'No tasks can be created from these notes.') {
            return [];
        }

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(rewrittenNote, 'text/html');
            const items = Array.from(doc.querySelectorAll('li'));
            return items.map(li => (li.textContent || '').trim()).filter(Boolean);
        } catch {
            return [];
        }
    }, [aiMode, rewrittenNote]);

    React.useEffect(() => {
        if (isOpen && note) {
            console.log('aiMode', aiMode);
            setOriginalText(note.notes || '');
            setHasError(false);
            // Clear any previous rewritten note when opening modal
            dispatch(clearRewrittenNote());
        }
    }, [isOpen, note, dispatch]);

    const handleClose = () => {
        onOpenChange({ open: false });
        setOriginalText('');
        dispatch(clearRewrittenNote());
    };

    // Remove image tags (especially base64 data URIs) from HTML to reduce prompt size
    const stripImageBlobs = html => {
        if (!html) return html;

        // Replace all <img> tags with a placeholder text
        // This regex matches img tags with any attributes (including base64 data URIs)
        const imgTagRegex = /<img[^>]*>/gi;
        return html.replace(imgTagRegex, '');
    };

    const handleRewrite = async () => {
        if (!originalText.trim()) return;

        try {
            // Remove image blobs before sending to API to reduce prompt size
            const textWithoutImages = stripImageBlobs(originalText);
            // Pass HTML content to the API along with the mode
            const result = await dispatch(RewriteNote({ note_text: textWithoutImages, mode: aiMode }));
            if (result.error) {
                setHasError(true);
            }
        } catch (error) {
            console.error('Error rewriting text:', error);
            setHasError(true);
        }
    };

    const handleReplace = async () => {
        if (!rewrittenNote) return;

        try {
            // The PatchNote API expects: { notes, notepad, id }
            await dispatch(
                PatchNote({
                    notes: rewrittenNote,
                    notepad: notepadId, // Use the notepadId prop
                    id: note?.id,
                }),
            );

            // Notify parent to update editor content in edit mode
            if (typeof onReplace === 'func' || typeof onReplace === 'function') {
                try {
                    onReplace(rewrittenNote);
                } catch (_) {}
            }
            toast.success('Note updated successfully');
            handleClose();
        } catch (error) {
            console.error('Error updating note:', error);
            toast.error('Failed to update note');
        }
    };

    const handleRegenerate = async () => {
        if (!originalText.trim()) return;

        try {
            setHasError(false);
            // Remove image blobs before sending to API to reduce prompt size
            const textWithoutImages = stripImageBlobs(originalText);
            // Pass HTML content to the API along with the mode
            const result = await dispatch(RewriteNote({ note_text: textWithoutImages, mode: aiMode }));
            if (result.error) {
                setHasError(true);
            }
        } catch (error) {
            console.error('Error regenerating text:', error);
            setHasError(true);
        }
    };

    const handleCopy = async () => {
        if (!rewrittenNote) return;

        // Extract plain text from HTML (strip HTML tags)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = rewrittenNote;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        try {
            // Use modern Clipboard API with multiple formats
            // This allows plain text editors to get plain text,
            // while rich text editors can use the HTML with formatting
            if (navigator.clipboard && window.ClipboardItem) {
                const clipboardItem = new ClipboardItem({
                    'text/plain': new Blob([plainText], { type: 'text/plain' }),
                    'text/html': new Blob([rewrittenNote], { type: 'text/html' }),
                });
                await navigator.clipboard.write([clipboardItem]);
            } else {
                // Fallback: Create a temporary div with HTML content and copy it
                // This preserves formatting when pasting into rich text editors
                const htmlDiv = document.createElement('div');
                htmlDiv.innerHTML = rewrittenNote;
                htmlDiv.style.position = 'fixed';
                htmlDiv.style.left = '-9999px';
                htmlDiv.style.top = '-9999px';
                document.body.appendChild(htmlDiv);

                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(htmlDiv);
                selection.removeAllRanges();
                selection.addRange(range);

                // Copy with HTML formatting
                document.execCommand('copy');

                selection.removeAllRanges();
                document.body.removeChild(htmlDiv);
            }
            toast.success('Copied to clipboard', { position: 'top-right' });
        } catch (err) {
            // Final fallback: just copy plain text
            try {
                await navigator.clipboard.writeText(plainText);
                toast.success('Copied to clipboard', { position: 'top-right' });
            } catch (e) {
                toast.error('Failed to copy to clipboard', { position: 'top-right' });
            }
        }
    };

    const handleAddNewEntry = async () => {
        if (!rewrittenNote) return;

        try {
            // Create a new entry in the notepad with the rewritten content using POST
            await dispatch(
                PostNote({
                    notes: rewrittenNote,
                    notepad: notepadId,
                }),
            );

            toast.success('New entry added successfully');
            handleClose();
        } catch (error) {
            console.error('Error creating new entry:', error);
            toast.error('Failed to create new entry');
        }
    };

    // Auto-trigger rewrite when modal opens (only once)
    React.useEffect(() => {
        if (isOpen && originalText && !rewrittenNote && !rewriteNoteLoading) {
            handleRewrite();
        }
    }, [isOpen, originalText]); // Removed rewrittenNote and rewriteNoteLoading dependencies

    if (!isOpen) return null;

    return (
        <>
            {isOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onClick={handleClose}>
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            maxWidth: '550px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            zIndex: 10000002,
                        }}
                        onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '32px' }}>
                            {/* Header */}
                            <div className="d-flex align-items-center mb-4">
                                {aiMode === 'summarize' || aiMode === 'summary' ? (
                                    <>
                                        <TextAlignLeft20Regular color="var(--Brand-Secondary)" className="me-2" />
                                        <h5
                                            className="mb-0"
                                            style={{
                                                color: 'var(--Brand-Primary)',
                                                fontSize: '18px',
                                                fontWeight: '600',
                                            }}>
                                            Summary
                                        </h5>
                                    </>
                                ) : aiMode === 'keypoints' || aiMode === 'tasks' ? (
                                    <>
                                        <TextBulletList20Regular color="var(--Brand-Secondary)" className="me-2" />
                                        <h5
                                            className="mb-0"
                                            style={{
                                                color: 'var(--Brand-Primary)',
                                                fontSize: '18px',
                                                fontWeight: '600',
                                            }}>
                                            {aiMode === 'tasks' ? 'Tasks' : 'Keypoints'}
                                        </h5>
                                    </>
                                ) : aiMode === 'table' ? (
                                    <>
                                        <TableSimple20Regular color="var(--Brand-Secondary)" className="me-2" />
                                        <h5
                                            className="mb-0"
                                            style={{
                                                color: 'var(--Brand-Primary)',
                                                fontSize: '18px',
                                                fontWeight: '600',
                                            }}>
                                            Table
                                        </h5>
                                    </>
                                ) : (
                                    // Default to 'rewrite' for any other value or undefined
                                    <>
                                        <Wand20Regular color="var(--Brand-Secondary)" className="me-2" />
                                        <h5
                                            className="mb-0"
                                            style={{
                                                color: 'var(--Brand-Primary)',
                                                fontSize: '18px',
                                                fontWeight: '600',
                                            }}>
                                            Rewrite
                                        </h5>
                                    </>
                                )}
                            </div>

                            {/* Content Area */}
                            <div
                                style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    minHeight: '120px',
                                    marginBottom: '24px',
                                }}>
                                <div
                                    style={{
                                        color: '#6c757d',
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                        fontFamily: 'Inter',
                                    }}>
                                    {rewriteNoteLoading ? (
                                        <div className="d-flex align-items-center">
                                            <AiOutlineLoading3Quarters className="rotateLoader me-2" />
                                            Rewriting with AI...
                                        </div>
                                    ) : hasError ? (
                                        <div style={{ color: '#dc3545' }}>
                                            <div className="d-flex align-items-center mb-2">
                                                <span style={{ marginRight: '8px' }}>⚠️</span>
                                                <strong>Failed to rewrite with AI</strong>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#6c757d' }}>Please try clicking "Regenerate" or check your connection.</div>
                                        </div>
                                    ) : rewrittenNote ? (
                                        aiMode === 'tasks' ? (
                                            extractedTasks.length > 0 ? (
                                                <ul style={{ paddingLeft: 0, marginBottom: 0 }}>
                                                    {extractedTasks.map((task, index) => (
                                                        <li key={index} className="d-flex align-items-start" style={{ marginBottom: '6px', listStyleType: 'none' }}>
                                                            <span
                                                                style={{
                                                                    width: '6px',
                                                                    height: '6px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'rgb(108, 117, 125)',
                                                                    display: 'inline-block',
                                                                    marginRight: '8px',
                                                                    marginTop: '7px',
                                                                }}
                                                            />
                                                            <span style={{ flex: 1 }}>{task}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowTaskSidebar && setShowTaskSidebar(task)}
                                                                style={{
                                                                    border: 'none',
                                                                    background: 'transparent',
                                                                    padding: 0,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    marginLeft: '12px',
                                                                }}
                                                                aria-label="Add task">
                                                                <Add20Regular color="var(--Brand-Secondary)" />
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <SafeHTML html={rewrittenNote} tag="div" />
                                            )
                                        ) : (
                                            <SafeHTML html={rewrittenNote} tag="div" />
                                        )
                                    ) : (
                                        'Arthur Miller was an American playwright who redefined the boundaries of modern drama and theatre. His works are studied in schools and universities around the world and are continually performed at the highest level.'
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    {/* <button
                                        onClick={handleAddNewEntry}
                                        className="btn btn-primary me-3"
                                        disabled={rewriteNoteLoading || !rewrittenNote}
                                        style={{
                                            backgroundColor: 'var(--Brand-Secondary)',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                        }}>
                                        {rewriteNoteLoading ? (
                                            <>
                                                <AiOutlineLoading3Quarters className="rotateLoader me-2" />
                                                Processing...
                                            </>
                                        ) : (
                                            'Add new entry'
                                        )}
                                    </button> */}
                                    <button
                                        onClick={handleReplace}
                                        className="btn btn-primary me-3"
                                        disabled={rewriteNoteLoading || !rewrittenNote}
                                        style={{
                                            backgroundColor: 'var(--Brand-Secondary)',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                        }}>
                                        {rewriteNoteLoading ? (
                                            <>
                                                <AiOutlineLoading3Quarters className="rotateLoader me-2" />
                                                Processing...
                                            </>
                                        ) : (
                                            'Replace'
                                        )}
                                    </button>
                                    <div disabled={rewriteNoteLoading || !rewrittenNote} onClick={handleCopy} role="button" className={styles.linkBtn + ' d-flex me-4'}>
                                        <Copy20Regular /> Copy
                                    </div>
                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement>
                                            <span
                                                className={styles.backToEntriesText}
                                                style={{
                                                    cursor: rewriteNoteLoading || !rewrittenNote ? 'not-allowed' : 'pointer',
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    opacity: rewriteNoteLoading || !rewrittenNote ? 0.5 : 1,
                                                }}>
                                                <SquareAdd20Regular className="me-2" /> Add
                                            </span>
                                        </MenuTrigger>
                                        <MenuPopover className={styles.modalMenuPopover} style={{ zIndex: 1 }}>
                                            <MenuList>
                                                <MenuItem disabled={rewriteNoteLoading || !rewrittenNote} onClick={() => rewrittenNote && setShowObsSidebar(rewrittenNote)}>
                                                    Convert to Observations
                                                </MenuItem>
                                                <MenuItem disabled={rewriteNoteLoading || !rewrittenNote} onClick={() => rewrittenNote && setShowTaskSidebar(rewrittenNote)}>
                                                    Convert to Tasks
                                                </MenuItem>
                                                <MenuItem disabled={rewriteNoteLoading || !rewrittenNote} onClick={() => rewrittenNote && setShowIRLSidebar(rewrittenNote)}>
                                                    Convert to IRL
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>

                                    {/* <div onClick={handleRegenerate} role="button" className={styles.linkBtn + ' d-flex'}>
                                        Regenerate
                                    </div> */}
                                </div>

                                <span onClick={handleClose} className={styles.linkBtn} role="button" disabled={rewriteNoteLoading}>
                                    Cancel
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RewriteAIModal;
